import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Trophy,
  Target,
  TrendingUp,
  MessageSquare,
  ArrowRight,
  Lightbulb,
  ThumbsUp,
  AlertCircle,
  RotateCcw,
  Home,
  Download,
  Share2,
  Copy,
  Check,
  Loader2,
  Link as LinkIcon,
  Play,
  Pause,
  Volume2,
  Pin,
  PinOff
} from "lucide-react";

export default function SessionResults() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [scenarioSettings, setScenarioSettings] = useState(null);
  const resultsRef = useRef(null);

  // Replay state
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const [isPinned, setIsPinned] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API}/roleplay/session/${sessionId}`, {
          credentials: "include"
        });
        if (response.ok) {
          const data = await response.json();
          setSession(data);
          // Fetch scenario settings for "Try Again"
          if (data.scenario_id) {
            try {
              const scenarioRes = await fetch(`${API}/scenarios/${data.scenario_id}`, {
                credentials: "include"
              });
              if (scenarioRes.ok) {
                setScenarioSettings(await scenarioRes.json());
              }
            } catch (e) { console.warn("Failed to fetch scenario settings (may have been deleted):", e); }
          }
        } else {
          toast.error("Session not found");
          navigate("/history");
        }
      } catch (error) {
        console.error("Failed to fetch session:", error);
        toast.error("Failed to load results");
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionId, navigate]);

  // Check recording availability and pin status
  useEffect(() => {
    const checkRecording = async () => {
      try {
        const res = await fetch(`${API}/recordings/exists/${sessionId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setHasRecording(data.exists);
        }
      } catch (e) { console.warn("Failed to check recording availability:", e); }
    };
    checkRecording();
  }, [sessionId]);

  useEffect(() => {
    if (session?.pinned) setIsPinned(true);
  }, [session]);

  // Audio playback handlers
  const handlePlayPause = () => {
    // Already have an audio element — just toggle play/pause
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      return;
    }

    // Prevent duplicate fetches
    if (audioLoading) return;
    setAudioLoading(true);

    fetch(`${API}/recordings/stream/${sessionId}`, { credentials: "include" })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio();
        audio.src = url;

        audio.ontimeupdate = () => {
          setCurrentTime(audio.currentTime);
          setIsPlaying(!audio.paused);
        };
        audio.onended = () => { setIsPlaying(false); setCurrentTime(0); };
        audio.onpause = () => setIsPlaying(false);
        audio.onplay = () => setIsPlaying(true);

        // WebM from MediaRecorder often has Infinity duration.
        // Seek to end to force browser to calculate real duration.
        audio.onloadedmetadata = () => {
          if (!Number.isFinite(audio.duration)) {
            audio.currentTime = 1e7;
            audio.addEventListener("timeupdate", function seekHandler() {
              audio.removeEventListener("timeupdate", seekHandler);
              setDuration(audio.duration);
              audio.currentTime = 0;
              audioRef.current = audio;
              setAudioLoading(false);
              audio.play();
            });
          } else {
            setDuration(audio.duration);
            audioRef.current = audio;
            setAudioLoading(false);
            audio.play();
          }
        };
      })
      .catch(() => { toast.error("Failed to load recording"); setAudioLoading(false); });
  };

  const handleSeek = (value) => {
    const time = value[0];
    if (audioRef.current && Number.isFinite(time) && Number.isFinite(duration) && duration > 0) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src.startsWith("blob:")) URL.revokeObjectURL(audioRef.current.src);
        audioRef.current = null;
      }
    };
  }, []);

  const handlePin = async () => {
    setPinLoading(true);
    try {
      const endpoint = isPinned ? "unpin" : "pin";
      const res = await fetch(`${API}/data-management/${endpoint}/${sessionId}`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        setIsPinned(!isPinned);
        toast.success(isPinned ? "Session unpinned" : "Session saved - won't be auto-deleted");
      }
    } catch {
      toast.error("Failed to update pin status");
    } finally {
      setPinLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (!resultsRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`roleplay-results-${sessionId}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Create share link
  const handleCreateShareLink = async () => {
    setShareLoading(true);
    try {
      const response = await fetch(`${API}/sessions/${sessionId}/share`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        const link = `${window.location.origin}/shared/${data.token}`;
        setShareLink(link);
        toast.success("Share link created!");
      } else {
        throw new Error("Failed to create share link");
      }
    } catch (error) {
      console.error("Share failed:", error);
      toast.error("Failed to create share link");
    } finally {
      setShareLoading(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!shareLink) return;
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session || !session.feedback) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold mb-2">No Results Yet</h2>
        <p className="text-muted-foreground mb-6">
          This session hasn't been completed yet or feedback is still processing.
        </p>
        <Link to="/history">
          <Button>View History</Button>
        </Link>
      </div>
    );
  }

  const { feedback, score, messages, scenario_name } = session;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="session-results-page">
      {/* Header with Export/Share buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Badge variant="secondary" className="mb-2">Session Complete</Badge>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{scenario_name}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Export PDF Button */}
          <Button 
            variant="outline" 
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>

          {/* Share Dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Share Results</DialogTitle>
                <DialogDescription>
                  Create a shareable link that allows others to view these results without logging in.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                {!shareLink ? (
                  <Button 
                    onClick={handleCreateShareLink} 
                    className="w-full"
                    disabled={shareLoading}
                  >
                    {shareLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <LinkIcon className="w-4 h-4 mr-2" />
                    )}
                    Generate Share Link
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Input 
                        value={shareLink} 
                        readOnly 
                        className="font-mono text-sm"
                      />
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={handleCopyLink}
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Anyone with this link can view the results.
                    </p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Button
            variant="outline"
            data-testid="try-again-btn"
            onClick={() => navigate("/roleplay/new", { state: { scenarioSettings } })}
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Try Again
          </Button>
          <Button
            variant={isPinned ? "default" : "outline"}
            onClick={handlePin}
            disabled={pinLoading}
            data-testid="pin-session-btn"
          >
            {pinLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : isPinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />}
            {isPinned ? "Saved" : "Save"}
          </Button>
          <Link to="/dashboard">
            <Button data-testid="back-to-dashboard-btn">
              <Home className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </Link>
        </div>
      </div>

      {/* Results Content - wrapped in ref for PDF export */}
      <div ref={resultsRef} className="space-y-6 bg-background p-4">
        {/* Score Overview */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Score */}
          <Card className="md:col-span-1 relative overflow-hidden" data-testid="main-score-card">
            <div className={`absolute top-0 left-0 right-0 h-1 ${getScoreBg(score)}`} />
            <CardContent className="p-6 text-center">
              <Trophy className={`w-12 h-12 mx-auto mb-4 ${getScoreColor(score)}`} />
              <p className={`font-heading text-6xl font-bold ${getScoreColor(score)}`}>
                {score}
              </p>
              <p className="text-muted-foreground mt-2">Overall Score</p>
              <Badge 
                className={`mt-4 ${
                  score >= 80 ? "bg-green-500/10 text-green-500" :
                  score >= 60 ? "bg-yellow-500/10 text-yellow-500" :
                  "bg-red-500/10 text-red-500"
                }`}
              >
                {score >= 80 ? "Excellent" : score >= 60 ? "Good" : "Needs Work"}
              </Badge>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {feedback.summary}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Metrics Grid */}
        {feedback.metrics && Object.keys(feedback.metrics).length > 0 && (
          <Card data-testid="metrics-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Skill Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(feedback.metrics).map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className={`font-mono font-bold ${getScoreColor(value)}`}>
                        {value}%
                      </span>
                    </div>
                    <Progress value={value} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strengths & Improvements */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Strengths */}
          <Card data-testid="strengths-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                <ThumbsUp className="w-5 h-5" />
                Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {feedback.strengths?.map((strength, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                    <span className="text-sm">{strength}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Areas for Improvement */}
          <Card data-testid="improvements-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
                <Lightbulb className="w-5 h-5" />
                Areas for Improvement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {feedback.areas_for_improvement?.map((area, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                    <span className="text-sm">{area}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Specific Feedback */}
        {feedback.specific_feedback?.length > 0 && (
          <Card data-testid="specific-feedback-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Specific Feedback
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {feedback.specific_feedback.map((item, i) => (
                <div key={i} className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm italic text-muted-foreground mb-2">
                    "{item.quote}"
                  </p>
                  <p className="text-sm font-medium">{item.feedback}</p>
                  {item.suggestion && (
                    <p className="text-sm text-primary mt-2">
                      💡 {item.suggestion}
                    </p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Next Steps */}
        {feedback.next_steps?.length > 0 && (
          <Card data-testid="next-steps-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-primary" />
                Recommended Next Steps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {feedback.next_steps.map((step, i) => (
                  <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-sm">{step}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Session Replay Player */}
        {hasRecording && (
          <Card data-testid="replay-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-primary" />
                Session Replay
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="w-12 h-12 rounded-full flex-shrink-0"
                  onClick={handlePlayPause}
                  disabled={audioLoading}
                  data-testid="replay-play-btn"
                >
                  {audioLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </Button>
                <div className="flex-1 space-y-1">
                  <Slider
                    value={[currentTime]}
                    max={Number.isFinite(duration) && duration > 0 ? duration : 100}
                    step={0.1}
                    onValueChange={handleSeek}
                    className="cursor-pointer"
                    data-testid="replay-scrubber"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Conversation Transcript */}
        <Card data-testid="transcript-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Conversation Transcript
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-4">
                {messages?.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : "bg-muted text-foreground rounded-bl-sm"
                      }`}
                    >
                      <p className="text-xs font-medium mb-1 opacity-70">
                        {msg.role === "user" ? "You" : "AI Client"}
                      </p>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
